import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, IconButton } from '~/chords';
import { DROPDOWN_SEARCH_DEFAULT_MAX_LENGTH } from '~/chords/DropdownSingleSelect/constants';
import { cn } from '~/lib/cn';

interface DropdownSearchProps {
  value: string;
  onChange: (value: string) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  focusOnOpen?: boolean;
  refocusTrigger?: number;
  maxLength?: number;
}

export function DropdownSearch({
  value,
  onChange,
  inputRef,
  focusOnOpen,
  refocusTrigger,
  maxLength = DROPDOWN_SEARCH_DEFAULT_MAX_LENGTH,
}: DropdownSearchProps) {
  const { t } = useTranslation('common');
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (focusOnOpen && refocusTrigger && refocusTrigger > 0) {
      timeoutRef.current = window.setTimeout(() => {
        inputRef?.current?.focus();
      }, 10);
    }

    return () => {
      window.clearTimeout(timeoutRef.current);
    };
  }, [inputRef, focusOnOpen, refocusTrigger]);

  const focusFirstOption = useCallback(() => {
    const selectContent = inputRef?.current
      ?.closest('[data-radix-popper-content-wrapper]')
      ?.querySelector('[data-radix-select-viewport] div[role="group"]');

    const firstOption = selectContent?.querySelector<HTMLElement>(
      'div:not([data-disabled])',
    );

    firstOption?.focus();
  }, [inputRef]);

  /**
   * Prevent "@radix-ui/react-select" from hijacking keystrokes for its navigation.
   * For instance, without this, typing the letter "a" would focus the cursor on
   * the first dropdown option starting with the letter "a".
   */
  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();

      // Keep focus on the input unless the Tab or ArrowDown key is pressed.
      if (!['Tab', 'ArrowDown'].includes(event.key)) {
        timeoutRef.current = window.setTimeout(() => {
          inputRef?.current?.focus();
        }, 10);
      }

      // Clear the search input when the Escape key is pressed.
      if (event.key === 'Escape' && value) {
        event.stopPropagation();
        onChange('');

        return false;
      }

      // Navigate to the first dropdown option when ArrowDown key is pressed.
      if (event.key === 'ArrowDown') {
        event.preventDefault();

        focusFirstOption();
      }
    },
    [onChange, inputRef, value, focusFirstOption],
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      onChange(event.target.value);
    },
    [onChange],
  );

  const handleClearSearchButtonClick = useCallback(() => {
    onChange('');

    timeoutRef.current = window.setTimeout(() => {
      inputRef?.current?.focus();
    }, 10);
  }, [onChange, inputRef]);

  const handleClearSearchButtonKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      // Navigate to the first dropdown option when ArrowDown key is pressed.
      if (['ArrowDown'].includes(event.key)) {
        focusFirstOption();
      }
    },
    [focusFirstOption],
  );

  return (
    <div className="relative flex items-center justify-between border-b-px p-md border-b-emphasis-subtle">
      <div
        aria-hidden
        className="pointer-events-none absolute pl-[1em] text-subtle"
      >
        <Icon type="magnifying-glass" variant="solid" size="sm" />
      </div>
      <input
        ref={inputRef}
        type="text"
        name="search"
        value={value}
        maxLength={maxLength}
        className={cn(
          'grow rounded-xs px-xl py-sm text-body-sm duration-300 ease-out text-default placeholder:text-subtle',
          'border outline-none bg-emphasis-default border-emphasis-subtle hover:bg-emphasis-strong hover:border-emphasis-strong',
        )}
        placeholder={t('dropdownTree.searchPlaceholder')}
        onKeyDown={handleInputKeyDown}
        onChange={handleInputChange}
        aria-label={t('dropdownTree.searchInputLabel')}
        role="searchbox"
      />
      {value ? (
        <div className="absolute right-[1.5em] text-subtle hover:text-icon-strong">
          <IconButton
            type="button"
            iconType="circle-xmark"
            size="small"
            aria-label={t('globalSearch.clearInput')}
            onClick={handleClearSearchButtonClick}
            onKeyDown={handleClearSearchButtonKeyDown}
          />
        </div>
      ) : null}
    </div>
  );
}

DropdownSearch.displayName = 'DropdownSearch';
